/* eslint-disable no-unused-vars */
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

import {
  fetchEmployeeById,
  updateEmployeeById,
  createEmployee,
  clearEmployee,
  checkEmployeeCompleteness,
} from "../../store/slices/employeeDetailSlice";
import { fetchBranchesByCompanyId } from "../../store/slices/branchSlice";
import { fetchCompanyByType } from "../../store/slices/companySlice";
import { fetchDepartments } from "../../store/slices/departmentSlice";
import { fetchJobTitles } from "../../store/slices/jobtitleSlice";
import { fetchClients } from "../../store/slices/clientSlice";
import {
  uploadEmployeeImage,
  fetchEmployeeImageById,
  clearEmployeeImage,
} from "../../store/slices/employeeImageSlice";

const Section = ({ title, children, icon }) => (
  <div className="mb-4">
    <div
      className="d-flex align-items-center px-3 py-2 rounded-2 mb-3"
      style={{ backgroundColor: "#1e293b" }}
    >
      <i
        className={`bi ${icon} text-white me-2`}
        style={{ fontSize: "0.85rem" }}
      ></i>
      <h6
        className="m-0 text-white text-uppercase"
        style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}
      >
        {title}
      </h6>
    </div>
    <div className="row g-3 px-2">{children}</div>
  </div>
);

const CustomInput = ({
  label,
  name,
  value,
  onChange,
  disabled,
  type = "text",
  col = "4",
  required = false,
  error = false,
  readOnly = false,
}) => (
  <div className={`col-md-${col}`}>
    <label
      className="extra-small mb-1 text-muted text-uppercase"
      style={{ fontSize: "0.7rem" }}
    >
      {label} {required && <span className="text-danger">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      className={`form-control form-control-sm shadow-none ${error ? "border-danger" : ""}`}
      disabled={disabled}
      readOnly={readOnly}
      style={{
        height: "32px",
        fontSize: "0.85rem",
        borderRadius: "8px",
        border: disabled
          ? "none"
          : error
            ? "1.5px solid #dc3545"
            : "1px solid rgba(0,0,0,0.1)",
        backgroundColor: disabled || readOnly ? "#f8fafc" : "#ffffff",
      }}
    />
  </div>
);

const EmployeeDetails = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const hasLoadedRef = useRef(false);

  const { employee } = useSelector((state) => state.employeeDetail);
  const { list: allOrgs = [] } = useSelector((state) => state.companyTypeList);
  const { list: allBranches = [] } = useSelector((state) => state.branch);
  const { list: departments = [] } = useSelector((state) => state.department);
  const { list: jobTitlesList = [] } = useSelector((state) => state.jobtitle);
  const { list: clientList = [] } = useSelector((state) => state.client);
  const image = useSelector((state) => state.employeeImage?.image);

  const [isEditing, setIsEditing] = useState(!id);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [preview, setPreview] = useState(
    "https://placehold.co/200x200?text=Avatar",
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    extension: "",
    employeeId: "",
    employeeStatus: "Active",
    employeeLeaveStatus: "Available",
    companyEmail: "",
    personalEmail: "",
    companyId: "",
    branchId: "",
    departmentId: "",
    jobTitle: "",
    clientCode: "",
    hiringDate: "",
    resignationDate: "",
    separationDate: "",
    terminationDate: "",
    probationaryDate: "",
    regularizationDate: "",
    remarks: "",
    assignmentType: "Internal",
    clientId: "",
    isSeasonal: false,
  });

  const formatDate = (date) =>
    date && date !== "string" ? date.slice(0, 10) : "";

  const selectStyle = (name) => ({
    height: "32px",
    fontSize: "0.85rem",
    borderRadius: "8px",
    border: !isEditing
      ? "none"
      : errors[name]
        ? "1.5px solid #dc3545"
        : "1px solid rgba(0,0,0,0.1)",
    backgroundColor: !isEditing ? "#f8fafc" : "#ffffff",
    paddingRight: isEditing ? "2rem" : "0.5rem",
  });

  useEffect(() => {
    dispatch(fetchCompanyByType());
    dispatch(fetchBranchesByCompanyId());
    dispatch(fetchJobTitles());
    dispatch(fetchClients());
    if (id) {
      dispatch(fetchEmployeeById(id));
      dispatch(fetchEmployeeImageById(id));
    }
    return () => {
      dispatch(clearEmployee());
      dispatch(clearEmployeeImage());
      hasLoadedRef.current = false;
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (form.companyId) dispatch(fetchDepartments(form.companyId));
  }, [form.companyId, dispatch]);

  useEffect(() => {
    if (employee) {
      const matchedJobTitle = jobTitlesList.find(
        (jt) => jt.id === employee.jobTitleId,
      );

      setForm({
        ...employee,
        firstName: employee.firstName ?? "",
        middleName:
          employee.middleName === "string" || !employee.middleName
            ? ""
            : employee.middleName,
        lastName: employee.lastName ?? "",
        extension:
          employee.extension === "string" || !employee.extension
            ? ""
            : employee.extension,
        personalEmail: employee.personalEmail ?? "",
        companyEmail: employee.companyEmail ?? "",
        companyId: employee.companyId ?? "",
        branchId: employee.branchId ?? "",
        departmentId: employee.departmentId ?? "",
        clientId: employee.clientId ?? "",
        jobTitle: matchedJobTitle ? matchedJobTitle.title : "",
        clientCode: employee.client?.clientCode || "",
        hiringDate: formatDate(employee.hiringDate),
        resignationDate: formatDate(employee.resignationDate),
        separationDate: formatDate(employee.separationDate),
        terminationDate: formatDate(employee.terminationDate),
        probationaryDate: formatDate(employee.probationaryDate),
        regularizationDate: formatDate(employee.regularizationDate),
        remarks: employee.remarks === "string" ? "" : (employee.remarks ?? ""),
        assignmentType: employee.assignmentType ?? "Internal",
        employeeStatus: employee.employeeStatus ?? "Active",
        employeeLeaveStatus: employee.employeeLeaveStatus ?? "Available",
        isSeasonal: employee.isSeasonal === true,
      });

      if (id && !hasLoadedRef.current) {
        hasLoadedRef.current = true;
        setIsEditing(false);
      }
    } else if (!id) {
      setForm({
        firstName: "",
        middleName: "",
        lastName: "",
        extension: "",
        employeeId: "",
        employeeStatus: "Active",
        employeeLeaveStatus: "Available",
        companyEmail: "",
        personalEmail: "",
        companyId: "",
        branchId: "",
        departmentId: "",
        jobTitle: "",
        clientCode: "",
        hiringDate: "",
        resignationDate: "",
        separationDate: "",
        terminationDate: "",
        probationaryDate: "",
        regularizationDate: "",
        remarks: "",
        assignmentType: "Internal",
        clientId: "",
        isSeasonal: false,
      });
      setIsEditing(true);
    }
  }, [employee, id, jobTitlesList]);

  useEffect(() => {
    if (image?.imageUrl && !selectedFile) setPreview(image.imageUrl);
  }, [image, selectedFile]);

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const validate = () => {
    let newErrors = {};
    if (!form.firstName) newErrors.firstName = true;
    if (!form.lastName) newErrors.lastName = true;
    if (!form.personalEmail) newErrors.personalEmail = true;
    if (!form.companyId) newErrors.companyId = true;
    if (!form.branchId) newErrors.branchId = true;
    if (!form.jobTitle) newErrors.jobTitle = true;
    if (!form.hiringDate) newErrors.hiringDate = true;

    if (form.assignmentType === "Internal") {
      if (!form.departmentId) newErrors.departmentId = true;
    } else {
      if (!form.clientId) newErrors.clientId = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStatusMsg({ text: "", type: "" });
    setForm((prev) => {
      let updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
        ...(name === "companyId" ? { departmentId: "", branchId: "" } : {}),
      };
      if (name === "clientId") {
        const client = clientList.find((c) => String(c.id) === String(value));
        updated.clientCode = client ? client.clientCode : "";
      }
      return updated;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      setStatusMsg({ text: "Missing required fields.", type: "danger" });
      return;
    }

    setIsSaving(true);
    setStatusMsg({ text: "", type: "" });

    const payload = {
      firstName: form.firstName,
      middleName: form.middleName || "",
      lastName: form.lastName,
      extension: form.extension || "",
      companyEmail: form.companyEmail || "",
      personalEmail: form.personalEmail,
      companyId: form.companyId,
      branchId: form.branchId,
      assignmentType: form.assignmentType,
      departmentId:
        form.assignmentType === "Internal" ? form.departmentId : null,
      clientId: form.assignmentType === "Deployed" ? form.clientId : null,
      isSeasonal: form.assignmentType === "Deployed" ? form.isSeasonal : false,
      jobTitleId:
        jobTitlesList.find((jt) => jt.title === form.jobTitle)?.id || null,
      hiringDate: form.hiringDate,
      probationaryDate: form.probationaryDate ? form.probationaryDate : null,
      regularizationDate: form.regularizationDate ? form.regularizationDate : null,
      resignationDate: form.resignationDate ? form.resignationDate : null,
      separationDate: form.separationDate ? form.separationDate : null,
      terminationDate: form.terminationDate ? form.terminationDate : null,
      remarks: form.remarks || "",
      employeeStatus: form.employeeStatus,
    };

    try {
      let result;
      let targetId = id;

      if (id) {
        result = await dispatch(
          updateEmployeeById({ id, data: payload }),
        ).unwrap();
      } else {
        result = await dispatch(createEmployee(payload)).unwrap();
        targetId = result.id;
        hasLoadedRef.current = false;
      }

      if (selectedFile) {
        const imageData = new FormData();
        imageData.append("file", selectedFile);
        await dispatch(
          uploadEmployeeImage({ employeeId: targetId, file: imageData }),
        ).unwrap();
        setSelectedFile(null);
      }

      await dispatch(checkEmployeeCompleteness(targetId)).unwrap();

      setStatusMsg({
        text: "Saved and validated successfully!",
        type: "success",
      });
      setIsEditing(false);

      if (!id && targetId) {
        navigate(`/employee/${targetId}`, { replace: true });
      } else {
        dispatch(fetchEmployeeById(targetId));
      }

      setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
    } catch (err) {
      setStatusMsg({
        text: typeof err === "string" ? err : err?.message || "Error saving.",
        type: "danger",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const badgeStyle = (() => {
    const s = (form.employeeStatus || "").toLowerCase();
    if (s === "active" || s === "complete")
      return { bg: "#dcfce7", text: "#15803d", border: "#bbf7d0" };
    if (s === "incomplete" || s === "inactive")
      return { bg: "#fee2e2", text: "#b91c1c", border: "#fecaca" };
    return { bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" };
  })();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleCancel = () => {
    if (!id) navigate("/employee");
    else {
      setIsEditing(false);
      setSelectedFile(null);
      hasLoadedRef.current = false;
      setErrors({});
      setStatusMsg({ text: "", type: "" });
      dispatch(fetchEmployeeById(id));
      
      dispatch(fetchEmployeeImageById(id));
    }
  };

  return (
    <div className="container-fluid py-2 px-0">
      <style>{`
        .extra-small { font-size: 0.7rem; letter-spacing: 0.02em; }
        .avatar-container { position: relative; width: 150px; height: 150px; margin: 0 auto; }
        .avatar-circle { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; border: 4px solid #fff; box-shadow: 0 4px 15px rgba(0,0,0,0.1); background: #f8fafc; }
        .upload-badge { position: absolute; bottom: 5px; right: 5px; width: 36px; height: 36px; background: #1e293b; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; border: 3px solid #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
        .status-badge-modern { display: inline-block; padding: 6px 14px; border-radius: 8px; font-size: 0.7rem; text-transform: uppercase; background: ${badgeStyle.bg}; color: ${badgeStyle.text}; border: 1.5px solid ${badgeStyle.border}; }
        .progress-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.7); z-index: 100; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
      `}</style>

      <div className="bg-white p-3 rounded-3 shadow-sm border mb-4 position-relative">
        {isSaving && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <div
          className="d-flex justify-content-between align-items-center px-3 py-2 rounded-2 mb-4"
          style={{ backgroundColor: "#1e293b", minHeight: "44px" }}
        >
          <div className="d-flex align-items-center">
            <i
              className="bi bi-person-vcard-fill text-white me-2"
              style={{ fontSize: "0.9rem" }}
            ></i>
            <h6
              className="m-0 text-white text-uppercase"
              style={{ fontSize: "0.75rem" }}
            >
              General Information
            </h6>
          </div>
          <div className="d-flex align-items-center gap-3">
            {statusMsg.text && (
              <span className={`text-uppercase small text-${statusMsg.type}`}>
                {statusMsg.text}
              </span>
            )}
            {!isEditing ? (
              <button
                type="button"
                className="btn btn-sm py-0 px-2 shadow-sm d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: "#334155",
                  border: "none",
                  width: "32px",
                  height: "28px",
                }}
                onClick={() => setIsEditing(true)}
              >
                <i className="bi bi-pencil-square text-white"></i>
              </button>
            ) : (
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-success btn-sm py-0 px-2"
                  style={{ width: "32px", height: "28px" }}
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  <i className="bi bi-check-lg text-white"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-sm py-0 px-2"
                  style={{
                    backgroundColor: "#64748b",
                    border: "none",
                    width: "32px",
                    height: "28px",
                  }}
                  onClick={handleCancel}
                >
                  <i className="bi bi-x-lg text-white"></i>
                </button>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row g-0">
            <div
              className="col-md-auto mb-4 text-center border-end pe-4"
              style={{ width: "260px" }}
            >
              <div className="avatar-container mb-3">
                <img src={preview} alt="Avatar" className="avatar-circle" />
                {isEditing && (
                  <div
                    className="upload-badge"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <i className="bi bi-camera-fill"></i>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={handlePhotoChange}
              />

              <div className="mb-4">
                {!isEditing ? (
                  <div className="status-badge-modern">
                    {form.employeeStatus}
                  </div>
                ) : (
                  <div className="text-start px-1">
                    <label className="extra-small mb-1 text-muted text-uppercase">
                      Status <span className="text-danger">*</span>
                    </label>
                    <select
                      name="employeeStatus"
                      className="form-select form-select-sm shadow-none"
                      value={form.employeeStatus}
                      onChange={handleChange}
                      style={selectStyle("employeeStatus")}
                    >
                      {[
                        "Active",
                        "Incomplete",
                        "Validated",
                        "Resigned",
                        "Deceased",
                        "Notice Period",
                        "Terminated",
                        "Probation",
                        "Awol",
                      ].map((s) => (
                        <option key={s} value={s}>
                          {s.toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="status-badge-modern mb-3">
                {form.employeeLeaveStatus || "Available"}
              </div>

              <div className="text-start mb-3 px-1">
                <label className="extra-small mb-1 text-muted text-uppercase">
                  Employee ID
                </label>
                <div
                  className="form-control border-0 shadow-none text-center fw-bold"
                  style={{
                    fontSize: "0.9rem",
                    backgroundColor: "#f1f5f9",
                    borderRadius: "8px",
                  }}
                >
                  {form.employeeId ? (
                    <>
                      ID: {form.employeeId} - {form.branch?.BranchCode || "HQ"}
                    </>
                  ) : (
                    "NEW RECORD"
                  )}
                </div>
              </div>

              <div className="text-start mb-1 px-1">
                <label className="extra-small mb-1 text-muted text-uppercase">
                  Assignment Type <span className="text-danger">*</span>
                </label>
                <select
                  name="assignmentType"
                  className="form-select form-select-sm shadow-none"
                  value={form.assignmentType}
                  onChange={handleChange}
                  disabled={!isEditing}
                  style={selectStyle("assignmentType")}
                >
                  <option value="Internal">Internal</option>
                  <option value="Deployed">Deployed</option>
                </select>
              </div>

              {form.assignmentType === "Deployed" && (
                <div className="text-start mb-3 px-1 d-flex align-items-center mt-2">
                  <input
                    type="checkbox"
                    id="isSeasonal"
                    name="isSeasonal"
                    className="form-check-input me-2"
                    checked={form.isSeasonal}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                  <label
                    htmlFor="isSeasonal"
                    className="extra-small text-muted text-uppercase m-0"
                  >
                    Seasonal Employee
                  </label>
                </div>
              )}
            </div>

            <div className="col ps-4">
              <Section title="Personal Information" icon="bi-person-circle">
                <CustomInput
                  label="First Name"
                  name="firstName"
                  required
                  value={form.firstName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.firstName}
                />
                <CustomInput
                  label="Middle Name"
                  name="middleName"
                  value={form.middleName}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Last Name"
                  name="lastName"
                  required
                  value={form.lastName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.lastName}
                />
                <CustomInput
                  label="Extension"
                  name="extension"
                  col="2"
                  value={form.extension}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Personal Email"
                  name="personalEmail"
                  col="10"
                  required
                  value={form.personalEmail}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.personalEmail}
                />
              </Section>

              <Section title="Employment Information" icon="bi-building">
                <div className="col-md-6">
                  <label className="extra-small mb-1 text-muted text-uppercase">
                    Company <span className="text-danger">*</span>
                  </label>
                  <select
                    name="companyId"
                    className={`form-select form-select-sm ${errors.companyId ? "border-danger" : ""}`}
                    value={form.companyId}
                    onChange={handleChange}
                    disabled={!isEditing}
                    style={selectStyle("companyId")}
                  >
                    <option value="">Select Company</option>
                    {allOrgs.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="extra-small mb-1 text-muted text-uppercase">
                    Branch <span className="text-danger">*</span>
                  </label>
                  <select
                    name="branchId"
                    className={`form-select form-select-sm ${errors.branchId ? "border-danger" : ""}`}
                    value={form.branchId}
                    onChange={handleChange}
                    disabled={!isEditing}
                    style={selectStyle("branchId")}
                  >
                    <option value="">Select Branch</option>
                    {allBranches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branchName || b.companyName}
                      </option>
                    ))}
                  </select>
                </div>

                {form.assignmentType === "Internal" ? (
                  <>
                    <div className="col-md-6">
                      <label className="extra-small mb-1 text-muted text-uppercase">
                        Department <span className="text-danger">*</span>
                      </label>
                      <select
                        name="departmentId"
                        className={`form-select form-select-sm ${errors.departmentId ? "border-danger" : ""}`}
                        value={form.departmentId}
                        onChange={handleChange}
                        disabled={!isEditing}
                        style={selectStyle("departmentId")}
                      >
                        <option value="">Select Department</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.departmentName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="extra-small mb-1 text-muted text-uppercase">
                        Job Title <span className="text-danger">*</span>
                      </label>
                      <select
                        name="jobTitle"
                        className={`form-select form-select-sm ${errors.jobTitle ? "border-danger" : ""}`}
                        value={form.jobTitle}
                        onChange={handleChange}
                        disabled={!isEditing}
                        style={selectStyle("jobTitle")}
                      >
                        <option value="">Select Job Title</option>
                        {jobTitlesList.map((jt) => (
                          <option key={jt.id} value={jt.title}>
                            {jt.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <CustomInput
                      label="Company Email"
                      name="companyEmail"
                      col="12"
                      value={form.companyEmail}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </>
                ) : (
                  <>
                    <div className="col-md-6">
                      <label className="extra-small mb-1 text-muted text-uppercase">
                        Payroll Group <span className="text-danger">*</span>
                      </label>
                      <select
                        name="clientId"
                        className={`form-select form-select-sm ${errors.clientId ? "border-danger" : ""}`}
                        value={form.clientId}
                        onChange={handleChange}
                        disabled={!isEditing}
                        style={selectStyle("clientId")}
                      >
                        <option value="">Select Payroll Group</option>
                        {clientList.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name || c.clientName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <CustomInput
                      label="Payroll Group Code"
                      name="clientCode"
                      value={form.clientCode}
                      onChange={handleChange}
                      disabled={!isEditing}
                      readOnly={true}
                      col="6"
                    />

                    <div className="col-md-6">
                      <label className="extra-small mb-1 text-muted text-uppercase">
                        Job Title <span className="text-danger">*</span>
                      </label>
                      <select
                        name="jobTitle"
                        className={`form-select form-select-sm ${errors.jobTitle ? "border-danger" : ""}`}
                        value={form.jobTitle}
                        onChange={handleChange}
                        disabled={!isEditing}
                        style={selectStyle("jobTitle")}
                      >
                        <option value="">Select Job Title</option>
                        {jobTitlesList.map((jt) => (
                          <option key={jt.id} value={jt.title}>
                            {jt.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <CustomInput
                      label="Company Email"
                      name="companyEmail"
                      col="6"
                      value={form.companyEmail}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </>
                )}
              </Section>

              <Section title="Employment Dates" icon="bi-calendar-event">
                <CustomInput
                  label="Hiring Date"
                  name="hiringDate"
                  type="date"
                  required
                  value={form.hiringDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                  error={errors.hiringDate}
                />
                <CustomInput
                  label="Probationary"
                  name="probationaryDate"
                  type="date"
                  value={form.probationaryDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Regularization"
                  name="regularizationDate"
                  type="date"
                  value={form.regularizationDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Resignation"
                  name="resignationDate"
                  type="date"
                  value={form.resignationDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Separation"
                  name="separationDate"
                  type="date"
                  value={form.separationDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <CustomInput
                  label="Termination"
                  name="terminationDate"
                  type="date"
                  value={form.terminationDate}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                <div className="col-12 mt-2 px-2">
                  <label className="extra-small mb-1 text-muted text-uppercase">
                    Remarks
                  </label>
                  <textarea
                    name="remarks"
                    rows={3}
                    value={form.remarks}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="form-control shadow-none"
                    style={{
                      fontSize: "0.85rem",
                      borderRadius: "8px",
                      border: !isEditing ? "none" : "1px solid rgba(0,0,0,0.1)",
                      backgroundColor: !isEditing ? "#f8fafc" : "#fff",
                    }}
                  />
                </div>
              </Section>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default EmployeeDetails;